import { useRef, useState } from "react";
import { FileText, FolderOpen, Table2, X } from "lucide-react";

const PHOTO_EXT = /\.(jpe?g|png|webp|heic|heif|tiff?|cr2|cr3|nef|arw|dng|orf|rw2|raf)$/i;

const Label = ({ children }) => (
  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 block mb-1">{children}</label>
);

function PickButton({ testId, icon: Icon, label, onClick, picked }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
        picked ? "border-emerald-700 text-emerald-300 bg-emerald-950/30" : "border-slate-700 text-slate-300 hover:bg-slate-800"
      }`}
      data-testid={testId}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="truncate max-w-[90%]">{label}</span>
    </button>
  );
}

export function PreschoolCreateForm({ onSubmit, onClose }) {
  const [name, setName] = useState("");
  const [sheet, setSheet] = useState(null);
  const [roster, setRoster] = useState(null);
  const [photos, setPhotos] = useState([]);
  const sheetRef = useRef(null);
  const rosterRef = useRef(null);
  const photosRef = useRef(null);

  const folders = new Set(photos.map((f) => (f.webkitRelativePath || "").split("/").slice(0, -1).join("/")));
  const ready = name.trim() && sheet && roster && photos.length > 0;

  const submit = () => {
    if (!ready) return;
    const ok = window.confirm(
      `Start "${name.trim()}"?\n\nSheet: ${sheet.name}\nNames: ${roster.name}\nPhotos: ${photos.length} in ${folders.size} folder(s)\n\nThe ZIP will be delivered as ${name.trim()}_Preschool.zip`,
    );
    if (ok) onSubmit({ name: name.trim(), sheetFile: sheet, rosterFile: roster, photoFiles: photos });
  };

  return (
    <div className="mb-8 border border-amber-500/30 bg-amber-500/5 rounded-md p-5 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="preschool-create-form">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Label>Task name <span className="text-red-400">*</span></Label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. PeterRabbit2026"
            className="w-full h-9 px-3 rounded-sm border border-slate-800 bg-slate-950 text-slate-100 text-sm focus:outline-none focus:border-amber-400"
            data-testid="preschool-name-input"
          />
        </div>
        <button type="button" onClick={onClose} className="ml-3 mt-5 text-slate-500 hover:text-slate-200" data-testid="preschool-create-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Names &amp; numbers sheet (PDF)</Label>
          <input ref={sheetRef} type="file" accept=".pdf,image/*" className="hidden"
            onChange={(e) => setSheet(e.target.files?.[0] || null)} data-testid="preschool-sheet-input" />
          <PickButton testId="preschool-sheet-pick" icon={FileText} picked={!!sheet}
            label={sheet ? sheet.name : "Pick scanned sheet"} onClick={() => sheetRef.current?.click()} />
        </div>
        <div>
          <Label>Names file (CSV / Excel)</Label>
          <input ref={rosterRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => setRoster(e.target.files?.[0] || null)} data-testid="preschool-roster-input" />
          <PickButton testId="preschool-roster-pick" icon={Table2} picked={!!roster}
            label={roster ? roster.name : "Pick names file"} onClick={() => rosterRef.current?.click()} />
        </div>
        <div>
          <Label>Photo folders</Label>
          <input ref={photosRef} type="file" webkitdirectory="true" multiple className="hidden"
            onChange={(e) => setPhotos(Array.from(e.target.files || []).filter((f) => PHOTO_EXT.test(f.name)))}
            data-testid="preschool-photos-input" />
          <PickButton testId="preschool-photos-pick" icon={FolderOpen} picked={photos.length > 0}
            label={photos.length ? `${photos.length} photos · ${folders.size} folder(s)` : "Pick root folder"}
            onClick={() => photosRef.current?.click()} />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Each number in the IND / SIB / GRAD columns marks the first photo of that person's run — every photo up to the next written
        number belongs to them. Output names: <span className="font-mono text-slate-300">First-Last-Image0284-P.jpg</span> (P individual · G grad) · siblings <span className="font-mono text-slate-300">Last-Image0244-S.jpg</span>.
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!ready}
          onClick={submit}
          className="px-5 h-10 rounded-md bg-amber-400 text-slate-950 text-[12px] font-bold uppercase tracking-widest hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="preschool-start-btn"
        >
          Upload &amp; read sheet
        </button>
      </div>
    </div>
  );
}
